import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { OrganizationsService } from '../data/providers/organizations.service';
import { Organization } from '../data/providers/organization.model';
import { TranslocoService } from '@jsverse/transloco';
import { select, Store } from '@ngrx/store';
import { userRoleNewSelector } from '../store/selectors/user-role.selectors';
import { selectBffBaseUrl } from '../store/selectors/api-config.selectors';
import { UserService } from './user.service';
import { UserInformationProvider } from '@xt/user-context';
import { USER_ROLE } from '../store/reducers/session-info.reducer';
import { ConsentApiService } from './api/consent-api.service';

export interface MandantOrganization extends Organization {
  active: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class UserMandantFacilitiesService {
  private readonly userContext = inject(UserInformationProvider);
  private readonly consentApiService = inject(ConsentApiService);
  private readonly organizationsService = inject(OrganizationsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly store = inject(Store);
  private readonly userService = inject(UserService);

  facilities$ = new BehaviorSubject<MandantOrganization[]>([]);
  activeFacilities$: Observable<MandantOrganization[]>;
  hasActiveFacilities$: Observable<boolean>;
  tooltipDisabledSharing$: Observable<string>;
  basePath: string;
  patientId: string;
  patient = this.userContext.patient;

  constructor() {
    this.store.pipe(selectBffBaseUrl).subscribe((url) => {
      this.basePath = `${url}`;
    });
    this.userService.resourceId$.subscribe((id) => (this.patientId = id));

    /** @todo move activeFacilities$ and hasActiveFacilities$ data to store */
    this.store
      .pipe(select(userRoleNewSelector))
      .pipe(take(1))
      .subscribe((role) => {
        if (
          role.some((item) =>
            [USER_ROLE.VERIFIED_REPRESENTATIVE, USER_ROLE.PATIENT].includes(
              item,
            ),
          )
        ) {
          if (role.includes(USER_ROLE.VERIFIED_REPRESENTATIVE)) {
            this.patientId = this.patient().id;
          }
          this.fetchUserFacilities();
        }
      });

    this.activeFacilities$ = this.facilities$.pipe(
      map((facilities) => facilities.filter(({ active }) => active)),
    );

    this.hasActiveFacilities$ = this.activeFacilities$.pipe(
      map((activeFacilities) => !!activeFacilities.length),
    );

    this.tooltipDisabledSharing$ = this.hasActiveFacilities$.pipe(
      switchMap((hasActiveFacilities) =>
        hasActiveFacilities
          ? of('')
          : this.translocoService.selectTranslate(
              'app.tooltip.sharing-disabled',
              {
                manageFacilitiesLink: `
            <a class="blue-link" href="/dashboard/profile?action=openManageFacilites">
              ${this.translocoService.translate('app.tooltip.manageFacilities')}
            </a>
          `,
              },
            ),
      ),
    );
  }

  private fetchUserFacilities() {
    this.consentApiService
      .getUserFacilitiesOidsFromPermisions$(this.patientId)
      .pipe(
        take(1),
        switchMap((oids) => {
          return oids
            ? this.organizationsService
                .searchItems$(
                  {
                    identifier: oids,
                    _include: 'Organization:partof',
                  },
                  'Organization',
                  true,
                )
                .pipe(
                  map((organizations) =>
                    organizations
                      .filter(
                        (org) =>
                          org._resource.type?.[0]?.coding?.[0]?.code === 'O0',
                      )
                      .map((org) => ({ ...org, active: true })),
                  ),
                )
            : of([]);
        }),
      )
      .subscribe((facilities) => this.facilities$.next(facilities));
  }

  public refreshUserFacilities() {
    this.fetchUserFacilities();
  }
}
